/**
 * Single source of truth for light/dark theme.
 *
 * During the redesign migration this drives BOTH theme systems so the whole
 * app stays in sync from one toggle:
 *   - NEW (redesigned, token-based): `data-theme` attribute on <html> +
 *     localStorage 'routes:theme'  → re-tones every `.rd` surface via tokens.
 *   - LEGACY (not-yet-redesigned page bodies): `.dark-mode` class on <body> +
 *     localStorage 'theme'         → drives the existing DarkMode.css overrides.
 *
 * Once all page bodies are redesigned, the legacy half (the body class, the
 * 'theme' key, and DarkMode.css) can be removed and only the data-theme path
 * remains.
 */

const NEW_KEY = 'routes:theme';
const LEGACY_KEY = 'theme';

export function getStoredTheme() {
  try {
    const v = localStorage.getItem(NEW_KEY) || localStorage.getItem(LEGACY_KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch (e) { /* ignore */ }
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch (e) { /* ignore */ }
  return 'light';
}

export function applyTheme(theme) {
  const t = theme === 'dark' ? 'dark' : 'light';
  // New token system
  document.documentElement.setAttribute('data-theme', t);
  // Legacy system (old page bodies + DarkMode.css)
  if (typeof document.body !== 'undefined' && document.body) {
    document.body.classList.toggle('dark-mode', t === 'dark');
  }
  try {
    localStorage.setItem(NEW_KEY, t);
    localStorage.setItem(LEGACY_KEY, t);
  } catch (e) { /* ignore */ }
  return t;
}

export function toggleTheme() {
  const current = document.documentElement.getAttribute('data-theme') === 'dark'
    || (typeof document.body !== 'undefined' && document.body && document.body.classList.contains('dark-mode'))
      ? 'dark'
      : 'light';
  return applyTheme(current === 'dark' ? 'light' : 'dark');
}
